import { useEffect, useState } from "react";
import { Trash2 } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";

const STATUS_STYLES = {
  new: "border-amber-400/50 text-amber-400",
  read: "border-white/20 text-slate-400",
  replied: "border-emerald-400/50 text-emerald-400",
  archived: "border-white/10 text-slate-600",
};

const fmt = (iso) =>
  iso
    ? new Date(iso).toLocaleString("en-US", { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" })
    : "";

export const EnquiriesTab = () => {
  const [enquiries, setEnquiries] = useState([]);

  const load = () => api.get("/enquiries").then((r) => setEnquiries(r.data)).catch(() => {});
  useEffect(() => {
    load();
  }, []);

  const setStatus = async (e, status) => {
    try {
      await api.patch(`/enquiries/${e.id}`, { status });
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  const remove = async (e) => {
    if (!window.confirm(`Delete the enquiry from ${e.name}? This cannot be undone.`)) return;
    try {
      await api.delete(`/enquiries/${e.id}`);
      toast.success("Enquiry deleted.");
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  return (
    <div data-testid="admin-enquiries-tab">
      <p className="text-[11px] font-mono uppercase tracking-[0.2em] text-slate-500 mb-5">
        {enquiries.length} messages in the inbox
      </p>
      <div className="space-y-3">
        {enquiries.map((e) => (
          <div key={e.id} data-testid={`admin-enquiry-card-${e.id}`} className="border border-white/10 bg-[#12151C] p-5">
            <div className="flex flex-wrap items-center gap-3 mb-3">
              <span className={`text-[9px] font-mono uppercase tracking-[0.2em] border px-2 py-1 ${STATUS_STYLES[e.status] || STATUS_STYLES.new}`}>
                {e.status}
              </span>
              <span className="text-[9px] font-mono uppercase tracking-[0.2em] border border-white/15 text-slate-400 px-2 py-1">
                {e.type === "service" ? e.service || "Service" : "Contact"}
              </span>
              <span className="text-[10px] font-mono text-slate-600 ml-auto">{fmt(e.created_at)}</span>
            </div>
            <p className="font-heading font-bold uppercase tracking-tight text-lg">
              {e.name} <span className="text-slate-500 font-mono text-xs normal-case tracking-normal ml-2">{e.email}</span>
            </p>
            <p className="mt-2 text-sm text-slate-300 leading-relaxed">{e.message}</p>
            <div className="mt-4 flex flex-wrap gap-2">
              {e.status !== "read" && (
                <button data-testid={`enquiry-read-${e.id}`} onClick={() => setStatus(e, "read")} className="text-[10px] font-mono uppercase tracking-[0.18em] border border-white/20 text-slate-300 hover:border-white/50 px-3 py-1.5 transition-colors">
                  Mark read
                </button>
              )}
              {e.status !== "replied" && (
                <button data-testid={`enquiry-replied-${e.id}`} onClick={() => setStatus(e, "replied")} className="text-[10px] font-mono uppercase tracking-[0.18em] border border-emerald-400/40 text-emerald-400 hover:bg-emerald-400/10 px-3 py-1.5 transition-colors">
                  Mark replied
                </button>
              )}
              {e.status !== "archived" && (
                <button data-testid={`enquiry-archive-${e.id}`} onClick={() => setStatus(e, "archived")} className="text-[10px] font-mono uppercase tracking-[0.18em] border border-white/10 text-slate-500 hover:text-slate-300 px-3 py-1.5 transition-colors">
                  Archive
                </button>
              )}
              {e.status !== "new" && (
                <button data-testid={`enquiry-reopen-${e.id}`} onClick={() => setStatus(e, "new")} className="text-[10px] font-mono uppercase tracking-[0.18em] border border-amber-400/40 text-amber-400 hover:bg-amber-400/10 px-3 py-1.5 transition-colors">
                  Reopen
                </button>
              )}
              <button
                data-testid={`enquiry-delete-${e.id}`}
                onClick={() => remove(e)}
                className="ml-auto flex items-center gap-1.5 text-[10px] font-mono uppercase tracking-[0.18em] border border-white/10 text-slate-500 hover:text-[#FF3B30] hover:border-[#FF3B30]/60 px-3 py-1.5 transition-colors"
              >
                <Trash2 size={12} /> Delete
              </button>
            </div>
          </div>
        ))}
        {enquiries.length === 0 && (
          <p className="border border-white/10 bg-[#12151C] p-10 text-center text-xs font-mono uppercase tracking-[0.2em] text-slate-500">
            Inbox zero. The wire is quiet.
          </p>
        )}
      </div>
    </div>
  );
};
